/* eslint-disable no-console */
export type ListenerDebugLogger = {
  log: (...args: unknown[]) => void
  info: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
}

const noopLogger: ListenerDebugLogger = {
  log: () => {},
  info: () => {},
  warn: () => {},
  error: () => {},
}

function getConsole(): Console | null {
  return typeof console !== 'undefined' ? console : null
}

const isDevelopment =
  typeof process !== 'undefined' && process.env && process.env.NODE_ENV !== 'production'

export function isListenerCountDebugEnabled(): boolean {
  if (isDevelopment) {
    return true
  }

  if (typeof window === 'undefined') {
    return false
  }

  try {
    const stored = window.localStorage?.getItem('rpaDebug')
    if (stored === '1') {
      return true
    }
  } catch (error) {
    // Access to localStorage can throw in some environments; ignore
  }

  try {
    const search = typeof window.location?.search === 'string' ? window.location.search : ''
    if (search) {
      const params = new URLSearchParams(search)
      if (params.get('rpaDebug') === '1') {
        return true
      }
    }
  } catch (error) {
    // Ignore URL parsing issues
  }

  return false
}

function getLogger(): ListenerDebugLogger {
  const consoleRef = getConsole()
  if (!consoleRef) {
    return noopLogger
  }

  return {
    log: (...args: unknown[]) => {
      consoleRef.log(...args)
    },
    info: (...args: unknown[]) => {
      if (typeof consoleRef.info === 'function') {
        consoleRef.info(...args)
      } else {
        consoleRef.log(...args)
      }
    },
    warn: (...args: unknown[]) => {
      if (typeof consoleRef.warn === 'function') {
        consoleRef.warn(...args)
      } else {
        consoleRef.log(...args)
      }
    },
    error: (...args: unknown[]) => {
      if (typeof consoleRef.error === 'function') {
        consoleRef.error(...args)
      } else {
        consoleRef.log(...args)
      }
    },
  }
}

function openGroup(label: string, enabled: boolean): void {
  if (!enabled) {
    return
  }

  const consoleRef = getConsole()
  if (!consoleRef) {
    return
  }

  const groupLabel = `[ListenerCount] ${label}`

  if (typeof consoleRef.groupCollapsed === 'function') {
    consoleRef.groupCollapsed(groupLabel)
    return
  }

  consoleRef.log(groupLabel)
}

function closeGroup(enabled: boolean): void {
  if (!enabled) {
    return
  }

  const consoleRef = getConsole()
  if (consoleRef && typeof consoleRef.groupEnd === 'function') {
    consoleRef.groupEnd()
  }
}

export function withListenerDebug<T>(label: string, fn: (logger: ListenerDebugLogger) => T): T {
  const enabled = isListenerCountDebugEnabled()

  if (!enabled) {
    return fn(noopLogger)
  }

  const logger = getLogger()
  openGroup(label, enabled)
  try {
    return fn(logger)
  } finally {
    closeGroup(enabled)
  }
}

export async function withListenerDebugAsync<T>(
  label: string,
  fn: (logger: ListenerDebugLogger) => Promise<T>,
): Promise<T> {
  const enabled = isListenerCountDebugEnabled()

  if (!enabled) {
    return fn(noopLogger)
  }

  const logger = getLogger()
  openGroup(label, enabled)
  try {
    return await fn(logger)
  } finally {
    closeGroup(enabled)
  }
}
